/**
 * Thermal paper → ESC/POS column counts (Font A, 12×24 dots).
 * 58mm ≈ 384 dots → 32 chars; 80mm ≈ 576 dots → 48 chars.
 */
export const PAPER_SIZE_COLUMNS = {
  "58mm": 32,
  "80mm": 48,
};

/** Most portable BLE thermal printers use 58mm rolls. */
export const DEFAULT_PAPER_SIZE = "58mm";

export const PAPER_SIZE_OPTIONS = [
  { id: "58mm", label: "58mm (32 chars)", columns: 32 },
  { id: "80mm", label: "80mm (48 chars)", columns: 48 },
];

/**
 * Resolve printable column count from printer config.
 * Prefers explicit `width`, then `paperSize`, then 58mm default.
 * @param {object} [config]
 * @returns {number}
 */
export function resolvePrintColumns(config = {}) {
  const explicit = Number(config.width);
  if (Number.isFinite(explicit) && explicit > 0) {
    return explicit;
  }

  const paperSize = config.paperSize || DEFAULT_PAPER_SIZE;
  if (PAPER_SIZE_COLUMNS[paperSize]) {
    return PAPER_SIZE_COLUMNS[paperSize];
  }

  return PAPER_SIZE_COLUMNS[DEFAULT_PAPER_SIZE];
}

/**
 * @param {string} paperSize
 * @returns {number}
 */
export function columnsForPaperSize(paperSize) {
  return PAPER_SIZE_COLUMNS[paperSize] ?? PAPER_SIZE_COLUMNS[DEFAULT_PAPER_SIZE];
}

/**
 * Bill line-item column widths scaled to paper width.
 * @param {number} columns
 */
export function getBillColumnLayout(columns) {
  if (columns <= 32) {
    return { qtyWidth: 2, priceWidth: 6, amountWidth: 6, gaps: 3 };
  }
  if (columns <= 35) {
    return { qtyWidth: 3, priceWidth: 6, amountWidth: 7, gaps: 3 };
  }
  return { qtyWidth: 3, priceWidth: 7, amountWidth: 7, gaps: 3 };
}

/**
 * Double-width header only fits when name length ≤ half the line.
 * @param {number} columns
 * @param {string} [name]
 */
export function shouldUseDoubleSizeHeader(columns, name = "") {
  const maxChars = Math.floor(columns / 2);
  return name.length > 0 && name.length <= maxChars;
}

/**
 * Normalize saved printer config (defaults for legacy rows missing paperSize/width).
 * @param {object} [config]
 */
export function normalizePrinterPrintConfig(config = {}) {
  let paperSize = config.paperSize;
  let widthRaw = Number(config.width);

  if (!paperSize || !PAPER_SIZE_COLUMNS[paperSize]) {
    if (widthRaw === 48 || widthRaw === 44) {
      paperSize = "80mm";
    } else if (widthRaw === 42) {
      // Previous app default (42) was too wide for typical 58mm BLE printers.
      paperSize = DEFAULT_PAPER_SIZE;
      widthRaw = NaN;
    } else {
      paperSize = DEFAULT_PAPER_SIZE;
    }
  }

  const width =
    Number.isFinite(widthRaw) && widthRaw > 0
      ? widthRaw
      : columnsForPaperSize(paperSize);

  return {
    ...config,
    paperSize,
    width,
  };
}

export function defaultPrinterPrintConfig(overrides = {}) {
  return normalizePrinterPrintConfig({
    paperSize: DEFAULT_PAPER_SIZE,
    width: PAPER_SIZE_COLUMNS[DEFAULT_PAPER_SIZE],
    ...overrides,
  });
}

/** True for 58mm / narrow thermal configs (≤32 cols). */
export function is58mmPrintConfig(config = {}) {
  const normalized = normalizePrinterPrintConfig(config);
  return (
    normalized.paperSize === "58mm" || resolvePrintColumns(normalized) <= 32
  );
}
